import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Gauge,
  Lightbulb,
  Lock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  TooltipProvider,
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/lib/i18n";
import {
  methodologyShortLabel,
  conflictsLabel,
  correctionsLabel,
} from "@/lib/researchTransparencyCopy";
import { Disclaimer } from "@/components/Disclaimer";
import { TransparencyPanel } from "@/components/TransparencyPanel";

/**
 * AIInsightsPanel — Phase 3 institutional narrative UI.
 *
 * Replaces the previous inline AI bullets/summary panel in-place.
 *
 *  - Free tier  → shows the legacy bullets (`ai.bullets` + summary teaser)
 *                 + upgrade CTA. No regression vs prior UX.
 *  - Pro/Premium → progressive-disclosure tabs (Summary / Bull / Bear /
 *                  Risks / Confidence / Why this score). Falls back to the
 *                  legacy bullets if the narrative payload is missing for
 *                  any reason.
 *
 * Design intent (per Phase 3 spec):
 *  - No KPI duplication. Narratives INTERPRET; metrics live in the
 *    Institutional Snapshot and the KPI grid.
 *  - Premium feel: tabs + accordion-like progressive disclosure, generous
 *    spacing, beginner-friendly tooltips for glossary terms.
 */
export default function AIInsightsPanel({
  ai,
  narrative,
  full,
  disclaimer,
  ticker,
  summaryStatus,
  narrativeStatus,
  // Phase 6.BI-19 — Top-level flag from /analyze. True when the
  // deterministic sanitizer filled in any thin/missing AI section.
  // Drives the "AI assisted" badge variant.
  structuredAiSanitized = false,
  // Phase 6.BI-24 — Async AI generation status. When this is
  // `ai_generating` or `cache_hit_needs_ai_repair`, the panel renders
  // a "Generating AI insights…" skeleton instead of the full narrative
  // tabs. The frontend (Analyze.js) polls `/ai-status` every 3s and
  // automatically re-renders with the final payload when ready.
  aiStatusFinal = null,
  aiGeneratingTimedOut = false,
  ws7Metadata = null,
  // Slice B/C v1.4.3 — logged-out AnalyzeLanding preview opts out of
  // the internal WS-1 disclosure so it can be rendered as a
  // dedicated compact panel below "Key drivers behind the score".
  hideWs1Disclosure = false,
  // Owner-approved logged-out preview flag: renders neutral-grey
  // blurred continuation bars beneath the readable summary so the
  // Free preview card visually communicates "hidden premium AI
  // analysis" without exposing fabricated readable claims. Never
  // enabled for the authenticated /analyze/:ticker page.
  showLockedContinuation = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasNarrative =
    !!narrative &&
    narrative.available !== false &&
    !!narrative.institutional_summary;

  // Phase 6.BI-24 — When the backend hasn't finished generating the
  // full AI narrative yet, render a calm skeleton instead of the
  // (possibly thin) placeholder content. Only applies to paid plans
  // (Free runs a synchronous lightweight summary inline).
  const isAiGenerating = full && (
    aiStatusFinal === "ai_generating"
    || aiStatusFinal === "cache_hit_needs_ai_repair"
  );
  if (isAiGenerating) {
    return (
      <PanelShell
        title={t("analyze.ai_extra.panel_title")}
        badge={t("analyze.ai_extra.generating_badge")}
        accent
        disclaimer={disclaimer}
        ticker={ticker}
        ws7Metadata={ws7Metadata}
        hideWs1Disclosure={hideWs1Disclosure}
      >
        <AiGeneratingSkeleton timedOut={aiGeneratingTimedOut} t={t} />
      </PanelShell>
    );
  }


  // Phase 6.Z + 6.BF + 6.BH: when the LLM was unavailable / timed out /
  // skipped for time budget, the backend serves a deterministic
  // templated summary AND flags `ai_summary_status`. We show a discreet
  // info banner so the user understands why the AI commentary is a
  // generic template rather than a fresh analysis. The page itself
  // still renders normally — valuation + charts + KPIs remain the
  // primary surface.
  //
  // Phase 6.BH — Status taxonomy is now 5 canonical strings:
  //   full_llm_success      → primary LLM worked (no banner needed)
  //   fallback_llm_success  → fallback LLM worked (subtle info banner)
  //   fallback_template     → both LLMs failed / timed out / quota
  //                           exhausted → templated summary served
  //   skipped_time_budget   → AI was never attempted (budget too tight)
  //   llm_error             → runtime error during AI generation
  // Older statuses (`available`, `skipped_timeout`, `unavailable`,
  // `skipped_provider_quota`) are still recognised for backwards
  // compatibility with cached responses + audit-log lookups.
  // Phase 6.BI-23 — Public AI banner suppression.
  //
  // Pro/Premium users must NEVER see internal-failure language:
  //   - "service recovering"
  //   - "temporarily unavailable"
  //   - "templated"
  //   - "fallback"
  //   - "deterministic"
  //   - "cancelled"
  //
  // The only state where a user-visible banner is appropriate is
  // `skipped_provider_quota` (an honest "AI is paused" message).
  // For every other "LLM failed but valuation pipeline succeeded"
  // case (fallback_template / skipped_time_budget / skipped_timeout
  // / llm_error / metric_narrative_success), we render no banner —
  // the badge already conveys "AI ASSISTED" and the analysis itself
  // is complete and trustworthy.
  const aiBanner = (() => {
    // No banner whenever the new public "metric_narrative_success"
    // status is in effect (this is the deterministic sanitizer
    // succeeding on a valid fact pack).
    if (summaryStatus === "metric_narrative_success") {
      return null;
    }
    // Phase 6.BI-25 — Honest quota-paused banner with calm copy.
    // We never expose internal "recovering" / "service" wording to
    // users; the message stays focused on the user-visible outcome
    // (valuation still accurate, AI text temporarily lighter).
    if (summaryStatus === "skipped_provider_quota") {
      return {
        tone: "warn",
        text: t("analyze.ai_extra.ai_paused_banner"),
      };
    }
    // Real LLM success — no banner.
    if (
      summaryStatus === "full_llm_success"
      || summaryStatus === "fallback_llm_success"
      || summaryStatus === "available"
    ) {
      return null;
    }
    // Phase 6.BI-23 — ALL legacy failure states now suppress the banner
    // and rely on the badge + the AI tabs themselves (which were
    // populated by the deterministic sanitizer). Internal failure
    // wording is for Owner Console only.
    return null;
  })();

  // ---- Free tier — locked tab shell ----
  if (!full) {
    return (
      <PanelShell
        title={t("analyze.ai_extra.panel_title")}
        badge={t("analyze.ai_extra.free_preview_badge")}
        disclaimer={disclaimer}
        ticker={ticker}
        ws7Metadata={ws7Metadata}
        hideWs1Disclosure={hideWs1Disclosure}
      >
        {aiBanner ? <AIStatusBanner banner={aiBanner} /> : null}
        <FreeInsightsTabs
          bullets={ai?.bullets || []}
          summary={ai?.summary || ""}
          onUpgrade={() => navigate("/billing")}
          t={t}
          showLockedContinuation={showLockedContinuation}
        />
      </PanelShell>
    );
  }

  // ---- Pro/Premium fallback: if narrative absent, show legacy + warning ----
  if (!hasNarrative) {
    // Phase 6.BI-12 — Banner correctness:
    //   * If AI itself succeeded (full_llm_success / fallback_llm_success)
    //     AND we have real bullets/summary, do NOT show
    //     "Institutional narrative is temporarily unavailable" — that
    //     phrase is reserved for the case where NO AI content was
    //     produced at all. Even though the dedicated narrative engine
    //     didn't run, the AI did, so the user is seeing genuine LLM
    //     commentary.
    //   * Only show the "narrative unavailable" warning when narrative
    //     pipeline failed AND the AI itself also failed (i.e. nothing
    //     useful was produced).
    const aiActuallyProducedContent = (
      (summaryStatus === "full_llm_success" || summaryStatus === "fallback_llm_success")
      && (((ai?.bullets || []).length > 0) || !!ai?.summary)
    );
    // Phase 6.BI-23 — Also treat metric_narrative_success and any
    // sanitizer-produced substantive AI block as "produced content";
    // suppress the "temporarily unavailable" banner entirely for the
    // partial-narrative branch too.
    const aiBlockHasMetricContent = (
      (
        summaryStatus === "metric_narrative_success"
        || structuredAiSanitized
      )
      && (
        (ai?.summary || "").length >= 150
        || (Array.isArray(ai?.bullets) && (ai.bullets || []).length >= 2)
      )
    );
    // Pro/Premium must NEVER see "narrative is temporarily unavailable"
    // when we have ANY usable AI content. Banner only renders when
    // there is literally nothing useful in the AI block AND the
    // upstream narrative pipeline also failed — a near-impossible
    // state given the BI-19 sanitizer.
    const narrativeBanner = (
      !aiActuallyProducedContent
      && !aiBlockHasMetricContent
      && !((ai?.summary || "").length >= 100)
      && (
        narrativeStatus === "unavailable"
        || narrativeStatus === "skipped_timeout"
        || narrativeStatus === "skipped_time_budget"
        || narrativeStatus === "llm_error"
      )
    ) ? aiBanner : aiBanner;
    // Phase 6.BI-23 — Public badge for the partial-narrative path.
    // Same policy as the full-narrative path: AI on real LLM,
    // AI ASSISTED on metric_narrative_success or any other paid
    // state (never expose "TEMPLATED").
    const fallbackBadge = (
      summaryStatus === "full_llm_success"
      || summaryStatus === "fallback_llm_success"
      || summaryStatus === "available"
    ) ? "INSTITUTIONAL · AI" : "INSTITUTIONAL · AI ASSISTED";
    return (
      <PanelShell title={t("analyze.ai_extra.panel_title")} badge={fallbackBadge} disclaimer={disclaimer} ticker={ticker} ws7Metadata={ws7Metadata} hideWs1Disclosure={hideWs1Disclosure}>
        {narrativeBanner ? <AIStatusBanner banner={narrativeBanner} /> : null}
        <ul className="mt-3 space-y-2 text-sm" data-testid="ai-insights-bullets">
          {(ai?.bullets || []).map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#3182CE] mt-0.5">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {ai?.summary ? (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground/95">
            {ai.summary}
          </p>
        ) : null}
      </PanelShell>
    );
  }

  // ---- Pro/Premium full narrative UI ----
  //
  // Phase 6.BI-23 — Public badge wording policy:
  //   - "INSTITUTIONAL · AI"          → real LLM content was produced
  //                                     (primary or fallback model success).
  //   - "INSTITUTIONAL · AI ASSISTED" → LLM failed/cancelled/timed out
  //                                     but the deterministic sanitizer
  //                                     built a complete metric-grounded
  //                                     narrative from the canonical
  //                                     fact pack. Drives off the new
  //                                     `metric_narrative_success` status.
  //                                     Honest signal that the analysis
  //                                     uses Stockaily's valuation model
  //                                     + financial metrics without
  //                                     exposing internal "templated"
  //                                     wording to the user.
  //
  // Anything else (no fact pack, no AI, etc.) is treated as the
  // "AI ASSISTED" branch by default — Pro/Premium must NEVER show
  // "TEMPLATED" / "FALLBACK" / internal-status wording publicly.
  const isRealAiContent =
    summaryStatus === "full_llm_success"
    || summaryStatus === "fallback_llm_success"
    || summaryStatus === "available";          // legacy success label
  const isMetricNarrative =
    summaryStatus === "metric_narrative_success";
  const badgeLabel = isRealAiContent
    ? "INSTITUTIONAL · AI"
    : isMetricNarrative
      ? "INSTITUTIONAL · AI ASSISTED"
      : "INSTITUTIONAL · AI ASSISTED"; // fallback safety net — never expose "TEMPLATED"
  return (
    <TooltipProvider delayDuration={150}>
      <PanelShell
        title={t("analyze.ai_extra.panel_title")}
        badge={badgeLabel}
        accent
        disclaimer={disclaimer}
        ticker={ticker}
        ws7Metadata={ws7Metadata}
        hideWs1Disclosure={hideWs1Disclosure}
      >
        {aiBanner ? <AIStatusBanner banner={aiBanner} /> : null}
        <NarrativeTabs narrative={narrative} />
      </PanelShell>
    </TooltipProvider>
  );
}

/**
 * AIStatusBanner — tiny, discreet info row shown when AI commentary is
 * degraded. Never red/scary — the user's analysis is still trustworthy;
 * we just signal honestly that the AI text is briefly lighter.
 */
function AIStatusBanner({ banner }) {
  const isWarn = banner?.tone === "warn";
  return (
    <div
      className={`mt-3 rounded-lg border px-3 py-2 text-[11.5px] flex items-start gap-2 ${
        isWarn
          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          : "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
      }`}
      data-testid="ai-status-banner"
      data-tone={banner?.tone || "info"}
    >
      <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{banner?.text}</span>
    </div>
  );
}

// ---------- Panel shell ----------

function PanelShell({ title, badge, accent, children, disclaimer, ticker, ws7Metadata = null, hideWs1Disclosure = false }) {
  const { t, locale } = useTranslation();
  // Phase 7.UI.13b — Localize the known deterministic disclaimer text
  // ("This is not financial advice.") emitted by the backend. We match
  // by exact known string so any future change to the backend wording
  // will simply pass through unchanged for safety.
  const localizedDisclaimer = (() => {
    if (!disclaimer) return disclaimer;
    if (locale === "de" && disclaimer === "This is not financial advice.") {
      return t("analyze.ai_extra.disclaimer_not_advice");
    }
    return disclaimer;
  })();
  return (
    <div
      className="rounded-2xl border border-border/70 bg-card/70 p-5 sm:p-6 shadow-[var(--app-shadow-elev-1)]"
      data-testid="ai-insights-panel"
      data-ticker={ticker}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles
            className={`w-4 h-4 ${accent ? "text-[#3182CE]" : "text-[#3182CE]"}`}
          />
          <div className="font-medium">{title}</div>
        </div>
        {badge ? (
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
            {badge}
          </Badge>
        ) : null}
      </div>
      {children}
      {localizedDisclaimer ? (
        <div className="mt-5 flex items-center gap-2 text-[11px] text-muted-foreground/80" data-testid="ai-insights-disclaimer">
          <ShieldCheck className="w-3.5 h-3.5" /> {localizedDisclaimer}
        </div>
      ) : null}
      {/* WS-1 canonical regulatory disclosure — rendered on every
          AIInsightsPanel instance (public + authed) UNLESS the caller
          opts out via `hideWs1Disclosure`. Slice B/C v1.4.3 opt-out:
          the logged-out AnalyzeLanding preview relocates this
          disclosure to a dedicated compact panel underneath
          "Key drivers behind the score" in the right column, so the
          disclosure never appears inside the AI Insights card and
          never overflows into the Revenue chart. Owner-approved. */}
      {hideWs1Disclosure ? null : (
      <div
        className="mt-3 pt-3 border-t border-border/50 space-y-1.5"
        data-testid="ai-insights-ws1-disclosure"
        data-ws1-surface="ai_insights_panel"
        data-ws1-ticker={ticker || ""}
      >
        <Disclaimer compact />
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-muted-foreground/70"
          data-testid="ai-insights-ws1-status-line"
        >
          <span data-ws1-status="methodology" data-ws1-value="methodology_active_v1">
            {t("research_transparency.methodology_label")}: {methodologyShortLabel(t, "stockaily.methodology.v1")}
          </span>
          <span aria-hidden>·</span>
          <span data-ws1-status="conflicts" data-ws1-value="no_compensation_from_issuer">
            {t("research_transparency.conflicts_label")}: {conflictsLabel(t)}
          </span>
          <span aria-hidden>·</span>
          <span
            data-ws1-status="corrections"
            data-ws1-value={ws7Metadata?.correction_notice ? "correction_notice_present" : "no_corrections_published"}
          >
            {t("research_transparency.corrections_label")}: {correctionsLabel(t, ws7Metadata?.correction_notice ?? null)}
          </span>
        </div>
      </div>
      )}
    </div>
  );
}

// ---------- Narrative tabs ----------

const SEV_COLOR = {
  high:   { fg: "#E5484D", bg: "rgba(229,72,77,0.08)",  border: "rgba(229,72,77,0.30)" },
  medium: { fg: "#E0B341", bg: "rgba(224,179,65,0.06)", border: "rgba(224,179,65,0.28)" },
  low:    { fg: "#A8AEB7", bg: "rgba(168,174,183,0.05)", border: "rgba(168,174,183,0.22)" },
};

// Phase 6.BI-20/22 — per-section color accents. Every tab now carries a
// SUBTLE permanent treatment (idle bg + idle border + idle fg). The
// active tab uses the stronger fields (bgActive ~13% for clearer
// distinction, border at 55%, full fg, glow + ring + bottom underline
// bar). Colours are low-saturation and keep the Bloomberg/institutional
// aesthetic — no neon, no heavy gradients.
const TAB_ACCENTS = {
  summary:     {
    fg: "#5BB7E0", fgIdle: "rgba(91,183,224,0.72)",
    glow: "rgba(91,183,224,0.18)",
    border: "rgba(91,183,224,0.65)", borderIdle: "rgba(91,183,224,0.18)",
    bgActive: "rgba(91,183,224,0.13)", bgIdle: "rgba(91,183,224,0.04)",
  },
  bull:        {
    fg: "#3FAE89", fgIdle: "rgba(63,174,137,0.72)",
    glow: "rgba(63,174,137,0.18)",
    border: "rgba(63,174,137,0.65)", borderIdle: "rgba(63,174,137,0.18)",
    bgActive: "rgba(63,174,137,0.13)", bgIdle: "rgba(63,174,137,0.04)",
  },
  opportunity: {
    fg: "#3FAE89", fgIdle: "rgba(63,174,137,0.72)",
    glow: "rgba(63,174,137,0.18)",
    border: "rgba(63,174,137,0.65)", borderIdle: "rgba(63,174,137,0.18)",
    bgActive: "rgba(63,174,137,0.13)", bgIdle: "rgba(63,174,137,0.04)",
  },
  bear:        {
    fg: "#E5484D", fgIdle: "rgba(229,72,77,0.74)",
    glow: "rgba(229,72,77,0.16)",
    border: "rgba(229,72,77,0.65)", borderIdle: "rgba(229,72,77,0.20)",
    bgActive: "rgba(229,72,77,0.13)", bgIdle: "rgba(229,72,77,0.04)",
  },
  risks:       {
    fg: "#E0B341", fgIdle: "rgba(224,179,65,0.78)",
    glow: "rgba(224,179,65,0.18)",
    border: "rgba(224,179,65,0.65)", borderIdle: "rgba(224,179,65,0.22)",
    bgActive: "rgba(224,179,65,0.13)", bgIdle: "rgba(224,179,65,0.04)",
  },
  confidence:  {
    fg: "#8E84E0", fgIdle: "rgba(142,132,224,0.74)",
    glow: "rgba(142,132,224,0.18)",
    border: "rgba(142,132,224,0.65)", borderIdle: "rgba(142,132,224,0.20)",
    bgActive: "rgba(142,132,224,0.13)", bgIdle: "rgba(142,132,224,0.04)",
  },
  drivers:     {
    fg: "#6FC9D5", fgIdle: "rgba(111,201,213,0.74)",
    glow: "rgba(111,201,213,0.18)",
    border: "rgba(111,201,213,0.65)", borderIdle: "rgba(111,201,213,0.20)",
    bgActive: "rgba(111,201,213,0.13)", bgIdle: "rgba(111,201,213,0.04)",
  },
};

// ---------- Free preview locked tab shell (Phase 8.LP.FREE-POLISH) ----------
//
// Renders a tab row that visually mirrors the paid NarrativeTabs
// structure so Free users immediately understand what deeper reasoning
// sits behind the upgrade. Only the "Summary" tab is interactive; the
// remaining five tabs (Bull, Bear, Risks, Confidence, Why this score)
// render as disabled Radix triggers with a small lock icon and muted
// styling. Because those triggers are `disabled`, Radix will never
// activate them, so their TabsContent panels are never rendered — this
// guarantees no premium AI text is passed via props or hidden in the
// DOM (no CSS-blur hiding of real reasoning). Below the Summary panel
// we render a persistent teaser block explaining what's locked plus a
// single Upgrade CTA inside the same card.
function FreeInsightsTabs({ bullets, summary, onUpgrade, t, ws7Metadata = null, showLockedContinuation = false }) {
  // Safe-default note (2026-08-01 legacy-test cleanup):
  //   `ws7Metadata` is referenced at the bottom of this function
  //   (below the free-preview blur/CTA block) to optionally render a
  //   compact <TransparencyPanel/>. The parent <AIInsightsPanel/>
  //   currently does NOT pass this prop down for the Free tier, so the
  //   default of `null` means the block never renders — identical
  //   observable behavior to before this default was declared. Prior
  //   to this default the reference threw `ReferenceError:
  //   ws7Metadata is not defined` in Jest and in browsers whose React
  //   error boundary silently swallowed it. This default ONLY
  //   eliminates the crash; it does not enable a new UI surface for
  //   Free users. If a future change wants to show WS-7 provenance to
  //   Free users, the parent must explicitly pass `ws7Metadata={…}`.
  const LOCKED_TABS = [
    { id: "bull",       label: t("analyze.ai.bull"),           icon: TrendingUp },
    { id: "bear",       label: t("analyze.ai.bear"),           icon: TrendingDown },
    { id: "risks",      label: t("analyze.ai.risks"),          icon: AlertTriangle },
    { id: "confidence", label: t("analyze.inst.confidence"),   icon: Gauge },
    { id: "drivers",    label: t("analyze.ai.why_this_score"), icon: Lightbulb },
  ];

  const summaryText = (summary || "").toString().trim();

  return (
    <Tabs
      defaultValue="summary"
      className="mt-4"
      data-testid="ai-narrative-tabs-free"
    >
      <div className="relative -mx-1 overflow-x-auto scrollbar-hide">
        <TabsList className="bg-secondary/30 border border-border/50 rounded-xl p-1 flex flex-nowrap gap-1 min-w-max h-auto">
          {/* Active — Summary */}
          {(() => {
            const accent = TAB_ACCENTS.summary;
            return (
              <TabsTrigger
                value="summary"
                data-testid="ai-tab-summary"
                className="group relative text-[12.5px] whitespace-nowrap px-3 py-1.5 rounded-lg transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out border data-[state=inactive]:bg-[var(--tab-bg-idle)] data-[state=inactive]:border-[var(--tab-border-idle)] data-[state=inactive]:text-[var(--tab-fg-idle)] data-[state=active]:bg-[var(--tab-bg-active)] data-[state=active]:border-[var(--tab-border-active)] data-[state=active]:text-[var(--tab-fg-active)] data-[state=active]:shadow-[0_0_0_1px_var(--tab-border-active),0_0_18px_var(--tab-glow)] data-[state=active]:font-semibold hover:border-[var(--tab-border-active)] hover:text-[var(--tab-fg-active)] after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-[5px] after:h-[2px] after:rounded-full after:opacity-0 after:transition-opacity after:duration-200 data-[state=active]:after:opacity-100 data-[state=active]:after:bg-[var(--tab-border-active)]"
                style={{
                  ["--tab-bg-idle"]: accent.bgIdle,
                  ["--tab-border-idle"]: accent.borderIdle,
                  ["--tab-fg-idle"]: accent.fgIdle,
                  ["--tab-bg-active"]: accent.bgActive,
                  ["--tab-border-active"]: accent.border,
                  ["--tab-fg-active"]: accent.fg,
                  ["--tab-glow"]: accent.glow,
                }}
              >
                <Sparkles
                  className="w-3.5 h-3.5 mr-1.5 shrink-0 transition-colors duration-150"
                  style={{ color: "currentColor" }}
                />
                <span className="leading-none">{t("analyze.ai.summary")}</span>
              </TabsTrigger>
            );
          })()}

          {/* Locked tabs — visually present, non-interactive */}
          {LOCKED_TABS.map((tab) => {
            const accent = TAB_ACCENTS[tab.id] || TAB_ACCENTS.summary;
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                disabled
                aria-disabled="true"
                data-testid={`ai-tab-${tab.id}-locked`}
                data-locked="true"
                title={t("analyze.ai_extra.locked_pro_tab_hint")}
                className="group relative text-[12.5px] whitespace-nowrap px-3 py-1.5 rounded-lg border cursor-not-allowed opacity-70 hover:opacity-90 transition-opacity data-[disabled]:pointer-events-none"
                style={{
                  backgroundColor: accent.bgIdle,
                  borderColor: accent.borderIdle,
                  color: accent.fgIdle,
                }}
              >
                <Icon
                  className="w-3.5 h-3.5 mr-1.5 shrink-0"
                  style={{ color: "currentColor" }}
                />
                <span className="leading-none">{tab.label}</span>
                <Lock
                  className="w-3 h-3 ml-1.5 shrink-0 text-muted-foreground/80"
                  aria-hidden
                />
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>

      {/* Only Summary content is ever rendered — locked triggers are
          disabled and therefore never activate their (non-existent)
          content panels. This is the guarantee that premium reasoning
          is never present in the DOM for Free users. */}
      <TabsContent value="summary" className="mt-4" data-testid="ai-tab-summary-content">
        {bullets.length > 0 ? (
          <ul className="space-y-2 text-sm" data-testid="ai-insights-bullets">
            {bullets.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: 0.16 + i * 0.06 }}
                className="flex gap-2"
              >
                <span className="text-[#3182CE] mt-0.5">•</span>
                <span>{b}</span>
              </motion.li>
            ))}
          </ul>
        ) : null}
        {summaryText ? (
          <p
            className="mt-3 text-sm leading-relaxed text-muted-foreground/95"
            data-testid="ai-insights-summary"
          >
            {summaryText}
          </p>
        ) : null}
        {showLockedContinuation ? (
          <LockedContinuationPreview />
        ) : null}
      </TabsContent>

      {/* Locked teaser block (persistent, does NOT depend on tab
          selection). Uses only generic copy — no premium text is ever
          rendered for Free users, so devtools inspection cannot reveal
          anything through blur removal. */}
      <div
        className="mt-5 rounded-xl border border-border/70 bg-secondary/30 p-4"
        data-testid="ai-insights-locked-teaser"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          <Lock className="w-3 h-3" />
          {t("analyze.ai_extra.locked_teaser_eyebrow")}
        </div>
        <p
          className="mt-2 text-sm leading-relaxed text-foreground/90"
          data-testid="ai-insights-locked-teaser-body"
        >
          {t("analyze.ai_extra.locked_teaser_body")}
        </p>
        {/* Placeholder lines — purely decorative shapes, no sensitive
            text. Height provides visual weight so the Free preview
            card feels comparable to the neighbouring Key Drivers card
            on desktop. */}
        <div className="mt-3 space-y-2" aria-hidden>
          <div className="h-2 rounded-full bg-muted/70 w-11/12" />
          <div className="h-2 rounded-full bg-muted/60 w-10/12" />
          <div className="h-2 rounded-full bg-muted/50 w-9/12" />
        </div>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {t("analyze.ai_extra.free_upgrade_blurb")}
          </span>
          <Button
            size="sm"
            onClick={onUpgrade}
            data-testid="ai-upgrade-cta"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {t("analyze.ai_extra.upgrade_btn")}
          </Button>
        </div>
      </div>
      {ws7Metadata && (
        <div className="mt-4" data-testid="ai-insights-ws7">
          <TransparencyPanel metadata={ws7Metadata} compact />
        </div>
      )}
    </Tabs>
  );
}

/**
 * LockedContinuationPreview — "hidden premium AI analysis" placeholder
 * rendered directly beneath the readable summary in the logged-out
 * /analyze landing preview.
 *
 * Owner-approved character (see attached OLD reference screenshot):
 *   - The UNDERLYING content is actual `<p>` text nodes with normal
 *     Stockaily prose typography (`text-sm leading-relaxed`), NOT
 *     rectangular skeleton bars. This is what gives the blur its
 *     natural word/line silhouette instead of the "loading bar"
 *     look that was rejected.
 *   - The filler text is deliberately Lorem-Ipsum so we NEVER emit a
 *     readable fabricated investment claim. Even at 0px blur, the
 *     paragraphs would clearly read as placeholder text, not
 *     research.
 *   - A container-level `blur-[5px]` (moderate Gaussian) plus
 *     reduced opacity keeps the paragraphs unreadable while
 *     preserving irregular right edges + real paragraph wrapping.
 *   - 4 paragraphs of varied sentence lengths so the visual rhythm
 *     matches real multi-paragraph analysis.
 *   - Colour is `text-muted-foreground/60` — pure neutral grey,
 *     no accent tint, no green/teal contamination.
 *   - Purely presentational: `aria-hidden` + `select-none` so it
 *     never leaks to SR / clipboard.
 */
function LockedContinuationPreview() {
  const paragraphs = [
    "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam eaque ipsa quae ab illo inventore.",
    "Neque porro quisquam est qui dolorem ipsum quia dolor sit amet consectetur adipisci velit, sed quia non numquam eius modi tempora incidunt.",
    "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias.",
    "Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae.",
  ];
  return (
    <div
      className="mt-6 space-y-4 select-none blur-[5px] opacity-80"
      aria-hidden="true"
      data-testid="ai-insights-locked-continuation"
    >
      {paragraphs.map((p, i) => (
        <p
          key={i}
          className="text-sm leading-relaxed text-muted-foreground/60"
          data-testid={`ai-insights-locked-continuation-group-${i}`}
        >
          {p}
        </p>
      ))}
    </div>
  );
}


function NarrativeTabs({ narrative }) {
  const { t } = useTranslation();
  const {
    institutional_summary,
    bull_case,
    bear_case,
    opportunity_reasoning,
    risk_reasoning,
    confidence_explanation,
    quality_drivers,
    explainability,
  } = narrative || {};

  const tabs = useMemo(() => {
    const arr = [
      { id: "summary",    label: t("analyze.ai.summary"),         icon: Sparkles },
      { id: "bull",       label: t("analyze.ai.bull"),            icon: TrendingUp },
      { id: "bear",       label: t("analyze.ai.bear"),            icon: TrendingDown },
      { id: "risks",      label: t("analyze.ai.risks"),           icon: AlertTriangle },
      { id: "confidence", label: t("analyze.inst.confidence"),    icon: Gauge },
      { id: "drivers",    label: t("analyze.ai.why_this_score"),  icon: Lightbulb },
    ];
    if (opportunity_reasoning) {
      arr.splice(3, 0, { id: "opportunity", label: t("analyze.ai.opportunity"), icon: TrendingUp });
    }
    return arr;
  }, [opportunity_reasoning, t]);

  return (
    <Tabs defaultValue="summary" className="mt-4" data-testid="ai-narrative-tabs">
      <div className="relative -mx-1 overflow-x-auto scrollbar-hide">
        <TabsList className="bg-secondary/30 border border-border/50 rounded-xl p-1 flex flex-nowrap gap-1 min-w-max h-auto">
          {tabs.map((t) => {
            const accent = TAB_ACCENTS[t.id] || TAB_ACCENTS.summary;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                /* Phase 6.BI-20/22 — Every tab carries a SUBTLE permanent
                   accent (idle: ~4% bg, ~18% border, 72% fg). Active
                   tab steps up:
                     - stronger tinted fill (~12% bg)
                     - full-strength accent border + ring
                     - bold accent foreground
                     - 2px bottom indicator bar (drawn via `after:`
                       pseudo-element) so the active tab is also
                       readable at a glance for users who can't perceive
                       small colour-saturation changes.
                   All styling comes from data-[state=*] Tailwind
                   utilities pointing at CSS custom properties so inline
                   styles never outrank the active treatment.
                */
                className="group relative text-[12.5px] whitespace-nowrap px-3 py-1.5 rounded-lg transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out border data-[state=inactive]:bg-[var(--tab-bg-idle)] data-[state=inactive]:border-[var(--tab-border-idle)] data-[state=inactive]:text-[var(--tab-fg-idle)] data-[state=active]:bg-[var(--tab-bg-active)] data-[state=active]:border-[var(--tab-border-active)] data-[state=active]:text-[var(--tab-fg-active)] data-[state=active]:shadow-[0_0_0_1px_var(--tab-border-active),0_0_18px_var(--tab-glow)] data-[state=active]:font-semibold hover:border-[var(--tab-border-active)] hover:text-[var(--tab-fg-active)] after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-[5px] after:h-[2px] after:rounded-full after:opacity-0 after:transition-opacity after:duration-200 data-[state=active]:after:opacity-100 data-[state=active]:after:bg-[var(--tab-border-active)]"
                style={{
                  // CSS custom properties only — actual styling is
                  // applied by data-[state=*] utilities above. Inline
                  // properties would otherwise outrank Tailwind on
                  // specificity and break the active treatment.
                  ["--tab-bg-idle"]: accent.bgIdle,
                  ["--tab-border-idle"]: accent.borderIdle,
                  ["--tab-fg-idle"]: accent.fgIdle,
                  ["--tab-bg-active"]: accent.bgActive,
                  ["--tab-border-active"]: accent.border,
                  ["--tab-fg-active"]: accent.fg,
                  ["--tab-glow"]: accent.glow,
                }}
                data-testid={`ai-tab-${t.id}`}
              >
                <t.icon
                  className="w-3.5 h-3.5 mr-1.5 shrink-0 transition-colors duration-150"
                  style={{ color: "currentColor" }}
                />
                <span className="leading-none">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>

      <TabsContent value="summary" className="mt-4">
        <NarrativeText
          text={institutional_summary}
          glossary={explainability}
          testId="narrative-summary"
        />
      </TabsContent>

      <TabsContent value="bull" className="mt-4">
        <SectionBadge tone="good" label={t("analyze.ai.bull_case")} />
        <NarrativeText
          text={bull_case}
          glossary={explainability}
          testId="narrative-bull"
        />
      </TabsContent>

      <TabsContent value="bear" className="mt-4">
        <SectionBadge tone="bad" label={t("analyze.ai.bear_case")} />
        <NarrativeText
          text={bear_case}
          glossary={explainability}
          testId="narrative-bear"
        />
      </TabsContent>

      {opportunity_reasoning ? (
        <TabsContent value="opportunity" className="mt-4">
          <SectionBadge tone="good" label={t("analyze.ai.opportunity_reasoning")} />
          <NarrativeText
            text={opportunity_reasoning}
            glossary={explainability}
            testId="narrative-opportunity"
          />
        </TabsContent>
      ) : null}

      <TabsContent value="risks" className="mt-4">
        <SectionBadge tone="warn" label={t("analyze.ai_extra.section_top_risks")} />
        {(() => {
          // Phase 6.BI-18 — Filter out malformed risk entries (no name AND
          // no why). Backend sanitizer guarantees the structured shape,
          // this is defensive belt-and-braces so the UI never renders an
          // empty card.
          const validRisks = (risk_reasoning || []).filter((r) => {
            if (!r || typeof r !== "object") return false;
            const name = (r.name || "").toString().trim();
            const why = (r.why || "").toString().trim();
            return name.length > 0 || why.length > 0;
          });
          if (validRisks.length === 0) {
            return (
              <p className="text-sm text-muted-foreground/80" data-testid="narrative-risks-empty">
                {t("analyze.ai_extra.no_risks")}
              </p>
            );
          }
          return (
            <ul className="space-y-2.5" data-testid="narrative-risks">
              {validRisks.map((r, i) => {
                const sev = (r.severity || "medium").toLowerCase();
                const c = SEV_COLOR[sev] || SEV_COLOR.medium;
                const name = (r.name || "Risk").toString().trim() || "Risk";
                const why = (r.why || "").toString().trim();
                return (
                  <li
                    key={i}
                    className="rounded-xl border p-3"
                    style={{ borderColor: c.border, background: c.bg }}
                    data-testid={`narrative-risk-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex w-1.5 h-1.5 rounded-full"
                        style={{ background: c.fg }}
                      />
                      <span className="text-sm font-medium" data-testid={`narrative-risk-${i}-name`}>
                        {name}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-[9.5px] uppercase tracking-wider"
                        style={{ color: c.fg, borderColor: c.border }}
                      >
                        {sev}
                      </Badge>
                    </div>
                    {why ? (
                      <p
                        className="mt-1.5 text-[13px] leading-snug text-muted-foreground/95"
                        data-testid={`narrative-risk-${i}-why`}
                      >
                        {why}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </TabsContent>

      <TabsContent value="confidence" className="mt-4">
        <SectionBadge tone="neutral" label={t("analyze.ai_extra.section_confidence")} />
        <NarrativeText
          text={confidence_explanation}
          glossary={explainability}
          testId="narrative-confidence"
        />
      </TabsContent>

      <TabsContent value="drivers" className="mt-4">
        <SectionBadge tone="neutral" label={t("analyze.ai_extra.section_drivers")} />
        <div className="grid sm:grid-cols-2 gap-3" data-testid="narrative-drivers">
          <DriversList
            title={t("analyze.ai_extra.drivers_positive")}
            items={quality_drivers?.positive || []}
            tone="good"
            t={t}
          />
          <DriversList
            title={t("analyze.ai_extra.drivers_negative")}
            items={quality_drivers?.negative || []}
            tone="bad"
            t={t}
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}

function SectionBadge({ tone, label }) {
  const c = SEV_COLOR[tone === "good" ? "low" : tone === "bad" ? "high" : tone === "warn" ? "medium" : "low"];
  const fg =
    tone === "good" ? "#3FAE89" : tone === "bad" ? "#E5484D" : tone === "warn" ? "#E0B341" : "#A8AEB7";
  return (
    <div className="mb-2 flex items-center gap-2">
      <span
        className="inline-flex w-1.5 h-1.5 rounded-full"
        style={{ background: fg }}
      />
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function DriversList({ title, items, tone, t }) {
  const fg = tone === "good" ? "#3FAE89" : "#E5484D";
  const ItemIcon = tone === "good" ? TrendingUp : TrendingDown;
  // Phase 6.BI-18 — defensive filter so malformed entries never produce
  // empty bullets. Backend sanitizer guarantees the shape, but we
  // belt-and-braces here.
  const cleanItems = (items || [])
    .map((it) => (typeof it === "string" ? it.trim() : String(it || "").trim()))
    .filter((it) => it.length > 0);
  return (
    <div className="rounded-xl border border-border/60 bg-secondary/20 p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <ItemIcon className="w-3 h-3" style={{ color: fg }} />
        {title}
      </div>
      {cleanItems.length === 0 ? (
        <p className="text-xs text-muted-foreground/80" data-testid="drivers-empty">
          {t ? t("analyze.ai_extra.no_items") : "No items identified."}
        </p>
      ) : (
        <ul className="space-y-1.5 text-[13px] leading-snug">
          {cleanItems.map((it, i) => (
            <li key={i} className="flex gap-2">
              <span style={{ color: fg }} className="mt-[2px]">•</span>
              <span className="text-muted-foreground/95">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Narrative text with inline glossary tooltips ----------

/**
 * Render narrative text and, for known glossary terms, wrap matched phrases
 * with a small (i) tooltip. Terms are matched case-insensitively but only
 * the first occurrence per term is decorated to avoid visual noise.
 */
function NarrativeText({ text, glossary, testId }) {
  // Phase 6.BI-22 — AI prose renders as CLEAN PLAIN TEXT.
  //
  // Previously each glossary term (P/E, ROIC, FCF…) was wrapped in a
  // tooltip with a blue dotted underline — which looked indistinguishable
  // from accidental hyperlinks ("Strong ROIC of 51.97%" with "ROIC"
  // underlined in blue). That clashed with the institutional aesthetic
  // and confused users into clicking, expecting navigation.
  //
  // Glossary tooltips still exist on the KPI tile grid below the
  // narrative (those use the small info icon pattern). The narrative
  // itself stays untouched — pure prose, no link-styled words.
  //
  // We also strip any markdown links `[label](url)` or raw HTML
  // anchors `<a href=...>label</a>` that may leak from LLM output.
  // The spec is explicit: "Strip or neutralize markdown links and
  // HTML links from AI output before rendering."

  // `glossary` and `testId` are still accepted to keep callers stable;
  // glossary is intentionally unused.
  void glossary; // explicit no-op for future contributors

  const cleanText = useMemo(() => {
    if (!text) return "";
    let s = String(text);
    // Strip markdown links: [label](url) → label
    s = s.replace(/\[([^\]]+)\]\((?:https?:\/\/|mailto:|#)[^)]*\)/gi, "$1");
    // Strip HTML anchors: <a ...>label</a> → label
    s = s.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, "$1");
    // Strip any stray HTML tags (defensive — LLM very rarely emits them)
    s = s.replace(/<\/?[a-zA-Z][^>]*>/g, "");
    // Strip URL strings that the LLM may have emitted as bare text
    // (uncommon at our prompt, but keeps the prose 100% link-free).
    s = s.replace(/\bhttps?:\/\/\S+/gi, "");
    // Collapse the whitespace left by any of the above
    s = s.replace(/[ \t]{2,}/g, " ").trim();
    return s;
  }, [text]);

  if (!cleanText) return null;

  return (
    <p
      className="text-[14px] leading-relaxed text-foreground/95"
      data-testid={testId}
    >
      {cleanText}
    </p>
  );
}

// ---------- Free-tier summary teaser (legacy preserved) ----------

function SummaryWithBlur({ summary }) {
  if (!summary) return null;
  const text = String(summary).trim();
  let splitAt = text.search(/[\.!?]\s/);
  if (splitAt < 30 || splitAt > 220) splitAt = Math.min(120, text.length - 1);
  const visible = text.slice(0, splitAt + 1).trim();
  const hidden = text.slice(splitAt + 1).trim();
  return (
    <div className="mt-4 text-sm leading-relaxed" data-testid="ai-insights-summary">
      <span data-testid="ai-insights-summary-preview">{visible}</span>
      {hidden ? (
        <>
          {" "}
          <span
            className="opacity-50 blur-[3px] select-none"
            aria-hidden="true"
            data-testid="ai-insights-summary-blurred"
          >
            {hidden}
          </span>
        </>
      ) : null}
    </div>
  );
}

// ---------- Phase 6.BI-24 — Generating skeleton ----------
//
// Rendered while the background AI job is still running. Visually
// calm: no progress bar, no scary copy. We use the existing token
// palette (border/secondary) and a subtle shimmer keyframe defined
// inline via Tailwind utilities.
function AiGeneratingSkeleton({ timedOut = false, t }) {
  return (
    <div
      className="mt-3 space-y-4"
      data-testid="ai-generating-skeleton"
      data-timed-out={timedOut ? "true" : "false"}
    >
      <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#3182CE]/40 bg-[#3182CE]/10"
          aria-hidden="true"
        >
          <Sparkles className="w-3 h-3 text-[#3182CE] animate-pulse" />
        </span>
        <span data-testid="ai-generating-label">
          {timedOut
            ? (t ? t("analyze.ai_extra.generating_timed_out") : "AI insights are still being generated. They will appear automatically when ready.")
            : (t ? t("analyze.ai_extra.generating_label") : "Generating AI insights\u2026")}
        </span>
      </div>

      <div className="space-y-2">
        <div className="h-3 rounded-md bg-secondary/40 animate-pulse w-11/12" />
        <div className="h-3 rounded-md bg-secondary/40 animate-pulse w-10/12" />
        <div className="h-3 rounded-md bg-secondary/40 animate-pulse w-9/12" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-border/60 bg-secondary/20 p-3 space-y-2">
          <div className="h-2.5 rounded bg-secondary/40 animate-pulse w-1/3" />
          <div className="h-3 rounded bg-secondary/40 animate-pulse w-11/12" />
          <div className="h-3 rounded bg-secondary/40 animate-pulse w-10/12" />
        </div>
        <div className="rounded-xl border border-border/60 bg-secondary/20 p-3 space-y-2">
          <div className="h-2.5 rounded bg-secondary/40 animate-pulse w-1/3" />
          <div className="h-3 rounded bg-secondary/40 animate-pulse w-11/12" />
          <div className="h-3 rounded bg-secondary/40 animate-pulse w-10/12" />
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground/80">
        {t ? t("analyze.ai_extra.generating_footnote") : "Valuation, score, and KPIs above are already finalized. The full AI narrative is being prepared in the background."}
      </div>
    </div>
  );
}
